import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { FlowType } from "@redotech/redo-model/return-flow";
import { ReturnTagSource, Tag, TagKind } from "@redotech/redo-model/tag";
import {
  AddReturnTagState,
  getTagTypeName,
} from "@redotech/redo-return-ui/flows/return-flow/add-return-tag";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { TagSelector } from "../../components/tag-selector/tag-selector";

export const AddReturnTagDetails = memo(function AddReturnTagDetails({
  stepSelect,
  state,
  setState,
  flowType,
}: StepTypeDetailsProps<AddReturnTagState>) {
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );

  const nextStepChange = useHandler((next?: StepId) => {
    setState((state) => ({ ...state, next }));
  });

  // Convert between the state's tag format and the Tag interface
  const currentTags = useMemo(
    () =>
      state.tags.map((tag) => ({
        name: tag.name,
        pillTheme: tag.color as PillTheme,
        source: ReturnTagSource.MERCHANT,
        kind: TagKind.RETURN,
      })),
    [state.tags],
  );

  const handleTagsChange = useHandler((tags: Tag[]) => {
    setState((state) => ({
      ...state,
      tags: tags.map((tag) => ({ name: tag.name, color: tag.pillTheme })),
    }));
  });

  const tagTypeName = getTagTypeName(flowType);

  return (
    <>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {tagTypeName} tags
        </Text>
        <TagSelector
          displayMode="badges"
          filterSystemTags
          onTagsChange={handleTagsChange}
          readOnly={!canEditReturnsSettings}
          tags={currentTags}
        />
        <Text fontSize="xxs" fontWeight="regular" textColor="tertiary">
          The tags that will be added to the {tagTypeName.toLowerCase()} when it
          hits this step.
        </Text>
      </Flex>

      <Divider />
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Next step
        </Text>
        {stepSelect({ value: state.next, valueChange: nextStepChange })}
        {flowType !== FlowType.FINALIZE_RETURN &&
          flowType !== FlowType.FINALIZE_CLAIM &&
          !state.next && (
            <Text fontSize="xs" textColor="error">
              Required
            </Text>
          )}
      </Flex>
    </>
  );
});
